import React from 'react';
import { Link } from 'react-router-dom';
import './HomePage.css';

export const HomePage: React.FC = () => {
  const converters = [
    // Encoders & Decoders Tools
    {
      id: 'base64-text-encoder',
      title: 'Base64 Text Encoder',
      description: 'Encode text to Base64 format or decode Base64 strings back to readable text with UTF-8 support.',
      link: '/base64-text-encoder'
    },
    {
      id: 'qr-code-generator',
      title: 'QR Code Generator',
      description: 'Generate customizable QR codes from text, URLs, WiFi credentials, and contact information with various error correction levels.',
      link: '/qr-code'
    },
    {
      id: 'url-encoder',
      title: 'URL Encoder/Decoder',
      description: 'Encode and decode URLs for safe transmission in web requests, handling special characters and parameters.',
      link: '/url-encoder'
    },
    {
      id: 'base64-image-encoder',
      title: 'Base64 Image Encoder',
      description: 'Convert images to Base64 data URIs for embedding in HTML/CSS or decode Base64 images back to files.',
      link: '/base64-image-encoder'
    },
    {
      id: 'jwt-token-encoder',
      title: 'JWT Token Encoder',
      description: 'Generate, encode, and decode JWT tokens for authentication and data exchange with custom payloads.',
      link: '/jwt-token-encoder'
    },
    {
      id: 'html-entity-encoder',
      title: 'HTML Entity Encoder',
      description: 'Encode HTML entities for safe display or decode them back to readable characters with extended symbol support.',
      link: '/html-entity-encoder'
    },
    {
      id: 'gzip-compression',
      title: 'GZip Compression',
      description: 'Compress and decompress text data using GZip algorithm with configurable compression levels and statistics.',
      link: '/gzip-compression'
    },
    {
      id: 'certificate-decoder',
      title: 'Certificate Decoder',
      description: 'Parse and decode SSL/TLS certificates in various formats to extract information about validity, issuer, and subject.',
      link: '/certificate-decoder'
    },
    // Formatters Tools
    {
      id: 'json-formatter',
      title: 'JSON Formatter',
      description: 'Format, minify, or validate JSON data with customizable indentation and key sorting for improved readability.',
      link: '/json-formatter'
    },
    {
      id: 'sql-formatter',
      title: 'SQL Formatter',
      description: 'Format and beautify SQL queries with support for multiple dialects (MySQL, PostgreSQL, SQLite) and keyword case options.',
      link: '/sql-formatter'
    },
    {
      id: 'xml-formatter',
      title: 'XML Formatter',
      description: 'Format, minify, or validate XML documents with customizable indentation for better structure visualization.',
      link: '/xml-formatter'
    },
    // Converters Tools
    {
      id: 'cron-parser',
      title: 'Cron Parser',
      description: 'Parse cron expressions into human-readable schedules and visualize upcoming execution times.',
      link: '/cron-parser'
    },
    {
      id: 'date-converter',
      title: 'Date Converter',
      description: 'Convert dates and timestamps between different formats including Unix timestamps, ISO 8601, and timezone conversions.',
      link: '/date-converter'
    },
    {
      id: 'json-table-converter',
      title: 'JSON to Table Converter',
      description: 'Convert flat or nested JSON data into a sortable, interactive HTML table.',
      link: '/json-table-converter'
    },
    {
      id: 'json-yaml-converter',
      title: 'JSON ↔ YAML Converter',
      description: 'Seamlessly convert between JSON and YAML formats with data integrity.',
      link: '/json-yaml-converter'
    },
    {
      id: 'number-base-converter',
      title: 'Number Base Converter',
      description: 'Convert numbers between binary, octal, decimal, and hexadecimal bases.',
      link: '/number-base-converter'
    },
    // Generators Tools
    {
      id: 'hash-generator',
      title: 'Hash/Checksum Generator',
      description: 'Generate cryptographic hashes (MD5, SHA-1, SHA-256, SHA-512) and checksums (CRC32) from text input for data integrity verification.',
      link: '/hash-generator'
    },
    {
      id: 'lorem-ipsum',
      title: 'Lorem Ipsum Generator',
      description: 'Generate placeholder Lorem Ipsum text with customizable paragraphs, sentences, or words for designs and mockups.',
      link: '/lorem-ipsum'
    },
    {
      id: 'password-generator',
      title: 'Password Generator',
      description: 'Generate secure random passwords with customizable length (8-128 characters) and character sets for enhanced account security.',
      link: '/password-generator'
    },
    {
      id: 'uuid-generator',
      title: 'UUID Generator',
      description: 'Generate Universally Unique Identifiers (UUIDs) in v1 (timestamp-based) or v4 (random) format with multiple formatting options.',
      link: '/uuid-generator'
    },
    // Graphic Tools
    {
      id: 'color-blindness-simulator',
      title: 'Color Blindness Simulator',
      description: 'Simulate different types of color vision deficiency (Protanopia, Deuteranopia, Tritanopia) to test your designs for accessibility compliance.',
      link: '/color-blindness-simulator'
    },
    {
      id: 'image-converter',
      title: 'Image Converter',
      description: 'Convert images between PNG, JPEG, and WebP formats with quality control and file size optimization for web delivery.',
      link: '/image-converter'
    },
    // Testers Tools
    {
      id: 'jsonpath-tester',
      title: 'JSONPath Tester',
      description: 'Test and validate JSONPath expressions against JSON data with real-time results, example queries, and comprehensive cheat sheet.',
      link: '/jsonpath-tester'
    },
    {
      id: 'regex-tester',
      title: 'Regular Expression Tester',
      description: 'Test regex patterns with match highlighting, capture groups, and multiple flags (global, multiline, case-insensitive, unicode, sticky, dotAll).',
      link: '/regex-tester'
    },
    {
      id: 'xml-tester',
      title: 'XML Tester',
      description: 'Test XPath queries, validate XML documents, and format/minify XML with comprehensive examples and error reporting.',
      link: '/xml-tester'
    },
    // Text Tools
    {
      id: 'text-escape',
      title: 'Text Escape/Unescape',
      description: 'Escape or unescape text for HTML entities, JavaScript/JSON strings, and URL encoding with support for multiple formats.',
      link: '/text-escape'
    },
    {
      id: 'list-compare',
      title: 'List Compare',
      description: 'Compare two lists to find differences, intersection, or union with clear visualization of unique and common items.',
      link: '/list-compare'
    },
    {
      id: 'markdown-preview',
      title: 'Markdown Preview',
      description: 'Write Markdown and see live HTML preview with support for basic syntax including headings, lists, links, images, and code blocks.',
      link: '/markdown-preview'
    },
    {
      id: 'text-analyzer',
      title: 'Text Analyzer & Utilities',
      description: 'Analyze text statistics (characters, words, lines), convert case formats (UPPER, lower, camelCase, snake_case, kebab-case), and normalize whitespace.',
      link: '/text-analyzer'
    },
    {
      id: 'text-compare',
      title: 'Text Compare',
      description: 'Compare two texts side-by-side with line-by-line diff view highlighting additions, deletions, and modifications.',
      link: '/text-compare'
    }
  ];

  return (
    <div className="page-container home-page">
      <div className="home-header">
        <div className="home-title">
          <h1>WebDevToys</h1>
          <p>Essential tools for developers to streamline daily workflows and ensure data accuracy across various formats.</p>
        </div>
      </div>

      <div className="converter-grid">
        {converters.map((converter) => (
          <div key={converter.id} className="converter-card">
            <h3>{converter.title}</h3>
            <p>{converter.description}</p>
            <Link to={converter.link} className="converter-link">
              Open {converter.title}
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
};